import { useUpdateEventWebsiteStateMutation } from '../../apps/admin/routes/WebsiteDesignerV2/WebsiteDesigner.graphql.generated';
import { EventWebsiteState } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCallback, useMemo } from 'react';

export const useWebsitePublishCapabilityFeature = (): Readonly<{
  eventExposure: Readonly<{
    value: boolean;
    isLoading: boolean;
  }>;
  eventPublishedState: Readonly<{
    value: boolean;
    update: (published: boolean) => Promise<unknown>;
    isLoading: boolean;
    isUpdating: boolean;
  }>;
}> => {
  const { eventInfo, loading, ssrEventInfo } = useEventInfo();

  const [isWebsitePublishedStateEnabled, isLoadingWebsitePublishedState] = useIsFeatureEnabled({ eventFeatureFlagKey: 'useWebsitePublishedState' });

  const [mutate, { loading: isUpdatingEventWebsiteState }] = useUpdateEventWebsiteStateMutation();

  const updateEventWebsiteState = useCallback(
    async (published: boolean) => {
      if (!eventInfo?.eventId) {
        return;
      }

      return mutate({
        variables: {
          payload: {
            eventId: eventInfo.eventId,
            websiteState: published ? EventWebsiteState.published : EventWebsiteState.draft,
          },
        },
      });
    },
    [eventInfo, mutate],
  );

  const eventExposure = useMemo(
    () => ({
      value: isWebsitePublishedStateEnabled,
      isLoading: isLoadingWebsitePublishedState,
    }),
    [isLoadingWebsitePublishedState, isWebsitePublishedStateEnabled],
  );

  const eventPublishedState = useMemo(
    () => ({
      value: (ssrEventInfo?.websiteState || eventInfo?.websiteState) === EventWebsiteState.published,
      update: updateEventWebsiteState,
      isLoading: loading,
      isUpdating: isUpdatingEventWebsiteState,
    }),
    [eventInfo?.websiteState, loading, updateEventWebsiteState, isUpdatingEventWebsiteState, ssrEventInfo],
  );

  return {
    eventExposure,
    eventPublishedState,
  };
};
